import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { memo, useCallback } from "react";
import { Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { FieldFormCheckboxRow } from "../FieldFormCheckboxRow";
import { FieldTypeEditorProps } from "../types";

export const FilePickerFieldTypeSpecificProps = memo(
  ({ field, onChange }: FieldTypeEditorProps) => {
    const updateProp = useCallback(
      <K extends keyof typeof field>(key: K, value: typeof field[K]) => {
        onChange({ ...field, [key]: value });
      },
      [field, onChange],
    );

    const setKindAt = useCallback(
      (index: number, value: string) => {
        const nextKinds = (field.kinds ?? []).map((kind, kindIndex) =>
          kindIndex === index ? value : kind,
        );
        updateProp("kinds", nextKinds);
      },
      [field.kinds, updateProp],
    );

    const removeKindAt = useCallback(
      (index: number) => {
        const nextKinds = (field.kinds ?? []).filter((_, kindIndex) => kindIndex !== index);
        updateProp("kinds", nextKinds.length ? nextKinds : undefined);
      },
      [field.kinds, updateProp],
    );

    const addKind = useCallback(() => {
      updateProp("kinds", [...(field.kinds ?? []), ""]);
    }, [field.kinds, updateProp]);

    return (
      <Stack spacing={1.5} sx={{ pt: 0.5 }}>
        <FieldFormCheckboxRow
          label='Allow multiple'
          checked={!!field.multiple}
          onChange={(checked) => updateProp("multiple", checked)}
        />
        <Typography variant='caption' color='text.secondary'>
          Kinds
        </Typography>
        <Stack spacing={1}>
          {(field.kinds ?? []).map((kind, index) => (
            <Stack key={index} direction='row' spacing={1} alignItems='center'>
              <TextField
                size='small'
                fullWidth
                value={kind}
                onChange={(e) => setKindAt(index, e.target.value)}
                placeholder='File kind'
              />
              <IconButton
                aria-label='Remove kind'
                size='small'
                onClick={() => removeKindAt(index)}
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button size='small' startIcon={<AddIcon />} onClick={addKind}>
          Add kind
        </Button>
      </Stack>
    );
  },
);
